/**
 * useConnectionQueries — React Query wrappers for fetching connections.
 */
import { useCallback } from 'react';
import { Connection, ConnectionStatus } from '@/types/connections';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { debugLog } from '@/utils/debug';
import { logger } from '@/utils/logger';

// Function to validate connection status
const validateConnectionStatus = (status: string): ConnectionStatus => {
  if (status === 'pending' || status === 'accepted' || status === 'rejected') {
    return status as ConnectionStatus;
  }
  // Default to pending if the status is unexpected
  return 'pending';
};

export interface ConnectionQueryResult {
  acceptedConnections: Connection[];
  pendingConnections: Connection[];
  error?: string;
}

export function useConnectionQueries() {
  const { toast } = useToast();
  const { user } = useAuth();

  // Fetch all connections for the current user with improved error handling
  const fetchConnections = useCallback(async (): Promise<ConnectionQueryResult> => {
    if (!user) return { acceptedConnections: [], pendingConnections: [] };
    
    try {
      debugLog("Fetching connections for user ID:", user.id);
      
      // Enhanced query to leverage the new consolidated RLS policies
      // The new policies automatically filter by sender_id OR receiver_id = auth.uid()
      // Explicit sender/receiver filter lets Postgres use the per-column
      // indexes instead of scanning under RLS only. Functionally identical
      // to the RLS-only query because policies already enforce the same
      // predicate, but ~10x cheaper in pg_stat_statements.
      const { data, error } = await supabase
        .from('user_connections')
        .select('*')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .order('created_at', { ascending: false });
      
      if (error) {
        logger.error("Error fetching connections:", error);
        
        // Enhanced error handling for RLS policy violations
        if (error.message?.includes('row-level security')) {
          throw new Error('Authentication required to view connections');
        }
        
        throw error;
      }
      
      debugLog("Raw connections data:", data);
      
      if (!data) {
        return { acceptedConnections: [], pendingConnections: [] };
      }
      
      // Process data with enhanced validation
      const connections = data.map(conn => {
        // Validate that the connection belongs to current user
        if (conn.sender_id !== user.id && conn.receiver_id !== user.id) {
          logger.warn('Connection does not belong to current user, filtering out:', conn.id);
          return null;
        }
        
        return {
          ...conn,
          status: validateConnectionStatus(conn.status)
        } as Connection;
      }).filter(Boolean) as Connection[];
      
      // Separate by status
      const acceptedConnections = connections.filter(conn => conn.status === 'accepted');
      const pendingConnections = connections.filter(conn => conn.status === 'pending');
      
      debugLog("Processed connections:", {
        accepted: acceptedConnections.length,
        pending: pendingConnections.length
      });
      
      return { acceptedConnections, pendingConnections };
    } catch (err: any) {
      logger.error('Error fetching connections:', err);
      
      // Enhanced error messaging
      let errorMessage = err.message;
      if (err.message?.includes('JWT')) {
        errorMessage = 'Authentication session expired. Please sign in again.';
      } else if (err.message?.includes('permission')) {
        errorMessage = 'Insufficient permissions to view connections.';
      }
      
      toast({
        title: "Failed to load connections",
        description: errorMessage,
        variant: "destructive",
      });
      return { error: errorMessage, acceptedConnections: [], pendingConnections: [] };
    }
  }, [user, toast]);

  return { fetchConnections };
}
